// ============================================================================
// ROOMS SERVICE
// Frontend API client for room management, device-room mapping, and reservations
// ============================================================================

import { api } from './api';

// ============================================================================
// TYPES
// ============================================================================

export interface Room {
  roomId: string;
  name: string;
  locationId: string;
  description?: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface RoomWithStatus extends Room {
  activeReservation: RoomReservation | null;
  deviceCount: number;
}

export interface RoomWithAvailability extends Room {
  available: boolean;
  reservedBySessionId?: string;
}

export interface DeviceRoomMapping {
  roomId: string;
  deviceId: string;
  assignedAt: string;
  assignedBy: string;
}

export interface RoomReservation {
  roomId: string;
  sessionId: string;
  startTime: string;
  endTime: string;
  leaseExpiresAt: string;
  leaseDurationMinutes: number;
  status: 'active' | 'released' | 'expired';
  reservedBy: string;
  reservedAt: string;
  reservedByName?: string;
}

// ============================================================================
// ADMIN API (system_admin only)
// ============================================================================

export async function listRoomsAdmin(organizationId?: string): Promise<RoomWithStatus[]> {
  const params = organizationId ? `?organizationId=${organizationId}` : '';
  const res = await api.get(`/api/rooms/admin/list${params}`);
  return res.data.rooms;
}

export async function createRoom(params: {
  name: string;
  locationId: string;
  description?: string;
  organizationId?: string;
}): Promise<Room> {
  const res = await api.post('/api/rooms/admin', params);
  return res.data.room;
}

export async function updateRoom(roomId: string, updates: {
  name?: string;
  locationId?: string;
  description?: string;
}): Promise<Room> {
  const res = await api.put(`/api/rooms/admin/${roomId}`, updates);
  return res.data.room;
}

export async function deleteRoom(roomId: string): Promise<void> {
  await api.delete(`/api/rooms/admin/${roomId}`);
}

export async function listRoomDevices(roomId: string): Promise<DeviceRoomMapping[]> {
  const res = await api.get(`/api/rooms/admin/${roomId}/devices`);
  return res.data.devices;
}

export async function assignDevicesToRoom(roomId: string, deviceIds: string[]): Promise<DeviceRoomMapping[]> {
  const res = await api.post(`/api/rooms/admin/${roomId}/devices`, { deviceIds });
  return res.data.devices;
}

export async function removeDeviceFromRoom(roomId: string, deviceId: string): Promise<void> {
  await api.delete(`/api/rooms/admin/${roomId}/devices/${deviceId}`);
}

// ============================================================================
// SESSION-FACING API (authenticated users)
// ============================================================================

export async function listAvailableRooms(organizationId?: string): Promise<Room[]> {
  const params = organizationId ? `?organizationId=${organizationId}` : '';
  const res = await api.get(`/api/rooms/available${params}`);
  return res.data.rooms;
}

export async function listAllRoomsWithStatus(organizationId?: string): Promise<RoomWithAvailability[]> {
  const params = organizationId ? `?organizationId=${organizationId}` : '';
  const res = await api.get(`/api/rooms/all${params}`);
  return res.data.rooms;
}

export async function getActiveReservation(roomId: string): Promise<RoomReservation | null> {
  const res = await api.get(`/api/rooms/${roomId}/reservation`);
  return res.data.reservation;
}

export async function getRoomSchedule(roomId: string): Promise<RoomReservation[]> {
  const res = await api.get(`/api/rooms/${roomId}/schedule`);
  return res.data.schedule;
}

export async function reserveRoom(roomId: string, params: {
  sessionId: string;
  startTime?: string;
  endTime?: string;
  leaseDurationMinutes?: number;
}): Promise<RoomReservation> {
  const res = await api.post(`/api/rooms/${roomId}/reserve`, params);
  return res.data.reservation;
}

export async function releaseRoom(roomId: string, sessionId: string): Promise<void> {
  await api.post(`/api/rooms/${roomId}/release`, { sessionId });
}

export async function renewRoomLease(roomId: string, params: {
  sessionId: string;
  additionalMinutes?: number;
}): Promise<RoomReservation> {
  const res = await api.post(`/api/rooms/${roomId}/renew-lease`, params);
  return res.data.reservation;
}
